// V2 版式：kpi-hero（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/kpi-hero.ts 逐坐标移植
// 3–4 个巨号数字 + 高度差柱体：accent 只给关键项（可比较时最大值，否则第一个），其余灰阶。
// 几何取 V2GEO["kpi-hero"]（deno dump 同源，勿手改数值）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { isLuxe, luxeExt } from "./luxe";
import { brutalTheme, termTheme, reportTheme, inkTheme, memphisTheme, memphisPalette } from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// deno dump 同源：与后端 kpi-hero.ts GEO 一一对应
const GEO = V2GEO["kpi-hero"] as any;

/** 从 value 字符串提取首个可比较数值（"128%" → 128；"1.2亿" → 1.2）——同后端 parseNum */
function parseNum(s: string): number | null {
  const m = s.replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
  return m ? Number(m[0]) : null;
}

export default function V2KpiHero({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // luxe：裸金巨号纪律——数字全部 accent；非关键柱用浅金 blush（kpi-hero.ts 分支）
  const luxe = isLuxe(skin);
  const ext = luxeExt(skin);
  // 第二批皮肤（043 §4.5–4.7）+ brutal/term 柱体分支
  const reportT = reportTheme(skin);
  const inkT = inkTheme(skin);
  const mpT = memphisTheme(skin);
  const brutalT = brutalTheme(skin);
  const termT = termTheme(skin);

  // metric block 逐块供料；保留原始 blocks 下标供编辑路径
  const metrics = c.blocks
    .map((b, bi) => ({ b, bi }))
    .filter((x): x is { b: { type: "metric"; label: string; value: string }; bi: number } => x.b.type === "metric")
    .slice(0, 4);

  const n = metrics.length;
  const contentW = W - 2 * m;
  const colW = n > 0 ? (contentW - (n - 1) * GEO.colGap) / n : 0;
  const baseline = GEO.content.bottom;
  const barW = colW * GEO.barWRatio;

  // 数值比较：全部可解析且有差异时按值比例，否则等高（同后端）
  const nums = metrics.map((x) => parseNum(x.b.value));
  const comparable = n > 0 && nums.every((v) => v !== null) && Math.max(...(nums as number[])) > Math.min(...(nums as number[]));
  const maxV = comparable ? Math.max(...(nums as number[])) : 1;
  const keyIdx = comparable ? nums.indexOf(maxV) : 0;

  // 4 列时巨号数字降档，仍守住 60pt 下限
  const heroSize = n <= 3 ? skin.scale.dataHero : Math.max(60, Math.round(skin.scale.dataHero * 0.85));

  /** 按列宽自适应数字字号（CJK≈1em、ASCII≈0.52em；宽度按 80% 列宽折算）——同后端 fitSize */
  const fitSize = (value: string): number => {
    const units = [...value].reduce((s, ch) => s + (/[ -~]/.test(ch) ? 0.52 : 1), 0);
    const fitted = Math.floor((colW * 0.8 * 72) / Math.max(units, 1));
    return Math.max(32, Math.min(heroSize, fitted));
  };

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "kpi-hero")} page={page} total={total} foot={footOf()} />

      {/* 页面标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(GEO.title.x), top: px(GEO.title.y),
          width: px(GEO.title.w), height: px(GEO.title.h),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.15,
        }}
      />

      {metrics.map(({ b, bi }, i) => {
        const x = m + i * (colW + GEO.colGap);
        const isKey = i === keyIdx;
        let barH: number;
        if (comparable) {
          const ratio = (nums[i] as number) / maxV;
          barH = Math.max(GEO.maxBarH * ratio, GEO.maxBarH * GEO.minBarRatio);
        } else {
          barH = GEO.flatBarH;
        }
        return (
          <div key={bi}>
            {/* 巨号数字（fontHero，valign bottom） */}
            <EditableText
              value={b.value} path={`blocks.${bi}.value`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(x), top: px(GEO.content.top),
                width: px(colW), height: px(GEO.numH),
                // report：关键数字 accentRed；ink：关键数字朱红；其余沿用 luxe/关键项 accent 链（kpi-hero.ts:100）
                fontSize: pt(fitSize(b.value)),
                color: reportT && isKey ? reportT.accentRed : inkT && isKey ? inkT.accent : luxe || isKey ? skin.accent : skin.text,
                fontFamily: skin.fontHero, lineHeight: 1,
                display: "flex", alignItems: "flex-end", whiteSpace: "nowrap",
              }}
            />
            {/* 小标签 */}
            <EditableText
              value={b.label} path={`blocks.${bi}.label`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(x), top: px(GEO.content.top + GEO.numH + GEO.labelGap),
                width: px(colW), height: px(GEO.labelH),
                fontSize: pt(skin.scale.body), color: skin.sub, fontFamily: skin.fontBody,
              }}
            />
            {/* 柱体：高度按数值比例拉开，基线对齐，左对齐（瑞士非对称）
                memphis：5 色轮用（页号轮转避免相邻页同序）；report：琥珀关键柱/深灰其余；
                ink：墨黑关键柱/黛灰其余（朱红只留给关键数字）；term：key 柱近黑填充 + 磷光绿描边（kpi-hero.ts:118-130） */}
            <div style={{
              position: "absolute", left: px(x), top: px(baseline - barH),
              width: px(barW), height: px(barH),
              background: mpT
                ? memphisPalette(mpT)[(page - 1 + i) % 5]
                : reportT ? (isKey ? reportT.accent : reportT.muted)
                : inkT ? (isKey ? inkT.ink : inkT.accentGrey)
                : isKey ? (brutalT ? skin.text : termT ? termT.paper : skin.accent)
                : ext ? ext.blush : brutalT ? skin.sub : skin.hairline,
              border: termT && isKey ? `1.5px solid ${skin.accent}` : undefined,
              borderRadius: mag ? px(skin.radius) : 0,
            }} />
          </div>
        );
      })}
    </>
  );
}
